import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";

export default function LoginForm() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/login`, {
        email: formData.email,
        password: formData.password,
      });

      if (response.status === 200) {
        // 1. Destructure the token and user details from your backend response
        const { token, user } = response.data;

        // 2. Save them to localStorage so the dashboard knows you are authenticated
        if (token) {
          localStorage.setItem("token", token);
          localStorage.setItem("userRole", user.role);
          localStorage.setItem("user", JSON.stringify(user));
        }

        setLoading(false);

        // 3. Navigate using an absolute path to avoid relative-routing loop glitches
        navigate("/dashboard");
      } else {
        setLoading(false);
        throw new Error("Failed to log in");
      }
    } catch (error) {
      console.error("Error during login submission:", error);
      setLoading(false);

      // Captures the actual text error message from your backend response if available
      const errorMsg =
        error.response?.data?.message ||
        "Invalid email or password combination.";
      setErrors({ auth: errorMsg });
    }
  };

  return (
    <div className="w-full relative max-w-[520px] mx-auto">
      {loading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <h1 className="text-3xl sm:text-7xl font-extrabold font-barlow text-black tracking-normal pb-2 mb-2 block clear-both">
        Sign In
      </h1>
      <p className="text-xs text-gray-500 font-montserrat leading-relaxed mb-8">
        Welcome to Spirit, where athletes compete, teams unite, and memories are
        made.
      </p>

      <form className="space-y-6" onSubmit={handleSubmit}>
        {errors.auth && (
          <div className="text-xs text-red-600 bg-red-50 p-2 rounded-sm font-medium">
            {errors.auth}
          </div>
        )}

        {/* Email Field */}
        <div className="flex flex-col gap-2 w-full">
          <label className="block text-xs font-bold text-black font-montserrat tracking-wider">
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="e.g. emmanuelmail@example.com"
            required
            className="w-full bg-[#CC5E15]/10 border-none text-gray-800 placeholder-black/40 text-xs rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-montserrat"
          />
        </div>

        {/* Password Field */}
        <div className="flex flex-col gap-2 w-full">
          <label className="block text-xs font-bold text-black font-montserrat tracking-wider">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="e.g. *********"
              required
              className="w-full bg-[#CC5E15]/10 border-none text-gray-800 placeholder-black/40 text-xs rounded-sm px-4 py-3 pr-12 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-black/50 hover:text-black flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* CTA Login Button */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            type="submit"
            className="flex-1 bg-black hover:bg-gray-900 text-white font-bold text-xs py-3 rounded-sm transition-colors tracking-widest font-montserrat"
          >
            Log In
          </button>
        </div>
      </form>

      {/* Sign Up Actions Link - CHANGED TO RELATIVE PATH */}
      <div className="flex items-center justify-center gap-1 mt-4 w-full">
        <span className="text-xs text-gray-500 font-montserrat">
          Don't Have an Account?
        </span>
        <button
          type="button"
          onClick={() => navigate("../signup")}
          className="text-black text-xs font-bold font-montserrat underline hover:text-gray-700 transition-colors bg-transparent p-0 border-none cursor-pointer"
        >
          Sign Up
        </button>
      </div>

      <div className="text-center mt-6">
        <p className="text-[9px] uppercase tracking-widest text-gray-400 font-medium">
          Together, we're redefining what student sport can be.
        </p>
      </div>
    </div>
  );
}
